/**
 * Combined ad-platform server event.
 * Facebook and OpenAI pixels still fire separately. Their server payloads are
 * mapped independently, then posted once to eventLogger.
 */

import { getCommonServerAttributes } from '@/shared/features/analytics/utils/commonAttributes';
import {
  resolveEventLoggerBaseUrl,
  toFormBody,
} from '@/shared/features/analytics/utils/eventLoggerHttp';

export type AdServerLogger = (payload: Record<string, unknown>) => void;

let adServerLogger: AdServerLogger | null = null;
let facebookPart: Record<string, unknown> | null = null;
let openaiPart: Record<string, unknown> | null = null;
let sharedEventId: string | null = null;

function createUuid(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Shared pixel + CAPI event_id. Reuses the in-flight id when a combined event is open. */
export function generateEventId(): string {
  return sharedEventId ?? createUuid();
}

export function setAdServerLogger(logger: AdServerLogger | null): void {
  adServerLogger = logger;
}

/** One UUID for both pixels and the combined server POST. */
export function startAdServerEvent(): string {
  facebookPart = null;
  openaiPart = null;
  sharedEventId = createUuid();
  return sharedEventId;
}

export function queueAdServerFacebookPart(part: Record<string, unknown>): void {
  facebookPart = part;
}

export function queueAdServerOpenAiPart(part: Record<string, unknown>): void {
  openaiPart = part;
}

export function removeEmptyParams(
  params: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }),
  );
}

function deliverAdServerEvent(payload: Record<string, unknown>): void {
  if (adServerLogger) {
    adServerLogger(payload);
    return;
  }

  const baseUrl = resolveEventLoggerBaseUrl();
  if (!baseUrl || typeof window === 'undefined') return;

  void fetch(`${baseUrl}/eventlogger/server/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: toFormBody({
      ...getCommonServerAttributes(),
      ...payload,
    }),
    credentials: 'include',
  }).catch(() => {
    // Fire-and-forget — legacy parity.
  });
}

/**
 * Posts at most one eventLogger payload for the current trackEvent.
 * Includes `metaEvent` / Facebook attrs only when Facebook mapped,
 * and `openAIEvent` / OpenAI attrs only when OpenAI mapped.
 */
export function flushAdServerEvent(): void {
  const facebook = facebookPart;
  const openai = openaiPart;
  const eventId = sharedEventId;
  facebookPart = null;
  openaiPart = null;
  sharedEventId = null;

  if (!facebook && !openai) return;

  const payload = removeEmptyParams({
    ...(openai ?? {}),
    ...(facebook ?? {}),
    _eventId:
      (typeof facebook?._eventId === 'string' && facebook._eventId) ||
      (typeof openai?._eventId === 'string' && openai._eventId) ||
      undefined,
    event_id: eventId,
    ...(facebook ? { metaEvent: true } : {}),
    ...(openai ? { openAIEvent: true } : {}),
  });

  if (!payload._eventId) return;
  deliverAdServerEvent(payload);
}
